import React from 'react';
import { COURSE_EDIT_TEST_IDS } from '@edusense/shared-constants';

/**
 * 🏗️ FSD Features Layer - Settings Tab Content
 *
 * 설정 탭 컨텐츠 (원본 reference 코드 기반)
 * 강의 공개/비공개, 댓글 허용 등 설정
 */

export interface SettingsTabContentProps {
  courseId: string;
  disabled?: boolean;
  isPublic?: boolean;
  allowComments?: boolean;
  allowDownloads?: boolean;
  requirePrerequisites?: boolean;
  completionCertificate?: boolean;
  onSettingChange?: (key: string, value: boolean) => void;
}

export const SettingsTabContent: React.FC<SettingsTabContentProps> = ({
  courseId,
  disabled = false,
  isPublic = true,
  allowComments = true,
  allowDownloads = false,
  requirePrerequisites = false,
  completionCertificate = true,
  onSettingChange,
}) => {
  const handleSettingChange = (key: string, value: boolean) => {
    if (onSettingChange) {
      onSettingChange(key, value);
    }
  };

  return (
    <div className="settings-tab-content" data-testid={COURSE_EDIT_TEST_IDS.SETTINGS_SECTION}>
      <h3 className="text-lg font-semibold mb-4">강의 설정</h3>

      <div className="space-y-4">
        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => handleSettingChange('isPublic', e.target.checked)}
              disabled={disabled}
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              data-testid={COURSE_EDIT_TEST_IDS.SETTINGS_PUBLIC_TOGGLE}
            />
            <div>
              <span className="font-medium text-gray-900">공개 강의</span>
              <p className="text-sm text-gray-500">모든 사용자가 이 강의를 볼 수 있습니다</p>
            </div>
          </label>
        </div>

        <div className="flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={allowComments}
              onChange={(e) => handleSettingChange('allowComments', e.target.checked)}
              disabled={disabled}
              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              data-testid={COURSE_EDIT_TEST_IDS.SETTINGS_COMMENTS_TOGGLE}
            />
            <div>
              <span className="font-medium text-gray-900">댓글 허용</span>
              <p className="text-sm text-gray-500">수강생들이 강의에 댓글을 달 수 있습니다</p>
            </div>
          </label>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4">수강 설정</h3>

        <div className="space-y-4">
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allowDownloads}
                onChange={(e) => handleSettingChange('allowDownloads', e.target.checked)}
                disabled={disabled}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                data-testid={COURSE_EDIT_TEST_IDS.SETTINGS_DOWNLOADS_TOGGLE}
              />
              <div>
                <span className="font-medium text-gray-900">자료 다운로드 허용</span>
                <p className="text-sm text-gray-500">수강생이 강의 자료를 다운로드할 수 있습니다</p>
              </div>
            </label>
          </div>

          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={requirePrerequisites}
                onChange={(e) => handleSettingChange('requirePrerequisites', e.target.checked)}
                disabled={disabled}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                data-testid={COURSE_EDIT_TEST_IDS.SETTINGS_PREREQUISITES_TOGGLE}
              />
              <div>
                <span className="font-medium text-gray-900">선수 강의 필수</span>
                <p className="text-sm text-gray-500">지정된 강의를 먼저 수료해야 수강 가능합니다</p>
              </div>
            </label>
          </div>

          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={completionCertificate}
                onChange={(e) => handleSettingChange('completionCertificate', e.target.checked)}
                disabled={disabled}
                className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                data-testid={COURSE_EDIT_TEST_IDS.SETTINGS_CERTIFICATE_TOGGLE}
              />
              <div>
                <span className="font-medium text-gray-900">수료증 발급</span>
                <p className="text-sm text-gray-500">강의 완료 시 수료증을 자동 발급합니다</p>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* 강의 정보 */}
      <div className="mt-8 border-t pt-6">
        <h4 className="text-md font-medium mb-4">강의 정보</h4>

        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-1">강의 ID</div>
            <div className="text-sm font-mono text-gray-700 select-all">{courseId}</div>
          </div>

          {/* 추가 가능한 유용한 정보들 */}
          <div className="text-xs text-gray-500 mt-4">
            * 설정 변경 사항은 저장 버튼을 눌러야 적용됩니다.
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsTabContent;
